/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Box, FlexContainer } from "@nordcloud/gnui";
import {
  RoleBindingByUser,
  RoleBindingsByUsersSortFields,
  SortOrder,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { Pagination } from "~/components/Pagination";
import { SortSelector } from "~/components/SortSelector";
import { TableWrapper } from "~/components/Tables";
import { useQueryState } from "~/hooks";
import { QuerySort } from "../types/QuerySort";
import { usersColumns } from "./UsersTableColumns";

const sortOptions = [
  {
    value: RoleBindingsByUsersSortFields.LastLoggedInAt,
    label: "Last Login Date",
  },
  {
    value: RoleBindingsByUsersSortFields.Subject,
    label: "User Name",
  },
];

type Props = {
  loading: boolean;
  data: RoleBindingByUser[];
  count: number;
  refetchUsers: () => void;
};
export function UsersTable({ loading, data, count, refetchUsers }: Props) {
  const { queryState, updateQueryState } = useQueryState<QuerySort>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const handleSortDirection = (order: SortOrder) => {
    updateQueryState({
      ...queryState,
      order,
    });
  };

  const handleSortField = (field: RoleBindingsByUsersSortFields) => {
    updateQueryState({
      ...queryState,
      field,
    });
  };

  return (
    <>
      <Box boxStyle="lightGrey" spacing="spacing04">
        <FlexContainer justifyContent="space-between">
          <SortSelector<RoleBindingsByUsersSortFields>
            options={sortOptions}
            value={queryState.field ?? RoleBindingsByUsersSortFields.Subject}
            direction={queryState.order ?? SortOrder.Asc}
            onSelect={handleSortField}
            onSortChange={handleSortDirection}
          />
        </FlexContainer>
      </Box>
      <LoaderWrap loading={loading} inContent>
        <TableWrapper<Partial<RoleBindingByUser & { id: string }>>
          loading={loading}
          data={data}
          columns={usersColumns({ refetchUsers })}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          stripped
        />
        <Pagination count={count} />
      </LoaderWrap>
    </>
  );
}
